import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { GiPeach, GiStoneTower } from "react-icons/gi";
import { Link, useNavigate } from "react-router-dom";
import "./Home.css";
import worldGif from "./world.gif";

const routes = [
  { key: "1", to: "/portfolio", label: "Portfolio", note: "Selected work and experience" },
  { key: "2", to: "/about", label: "About", note: "Background, skills, and approach" },
  { key: "3", to: "/contact", label: "Contact", note: "Start a conversation" },
];

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

export default function Home() {
  const navigate = useNavigate();
  const go = useCallback((path) => navigate(path), [navigate]);
  const titleRef = useRef(null);
  const [signalActive, setSignalActive] = useState(false);
  const [motionAllowed, setMotionAllowed] = useState(() => {
    if (typeof window.matchMedia !== "function") return true;
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = event.target?.tagName?.toLowerCase?.() || "";
      if (tag === "input" || tag === "textarea" || event.target?.isContentEditable) return;

      const destination = routes.find((route) => route.key === event.key);
      if (destination) go(destination.to);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [go]);

  useEffect(() => {
    const syncMotion = (event) => setMotionAllowed(event.detail?.enabled !== false);
    window.addEventListener("site-motion-change", syncMotion);
    return () => window.removeEventListener("site-motion-change", syncMotion);
  }, []);

  useEffect(() => {
    if (!motionAllowed) {
      setSignalActive(false);
      return undefined;
    }

    let scheduleTimer;
    let releaseTimer;
    let echoTimer;
    let echoReleaseTimer;

    const setSignalVariables = (intensity = 1) => {
      const title = titleRef.current;
      if (!title) return;

      const shift = randomBetween(14, 30) * intensity * (Math.random() > 0.5 ? 1 : -1);
      title.style.setProperty("--home-glitch-a", `${shift}px`);
      title.style.setProperty("--home-glitch-b", `${shift * -0.72}px`);
      title.style.setProperty("--home-glitch-c", `${shift * 0.38}px`);
      title.style.setProperty("--home-slice-a-top", `${randomBetween(5, 18)}%`);
      title.style.setProperty("--home-slice-a-bottom", `${randomBetween(69, 80)}%`);
      title.style.setProperty("--home-slice-b-top", `${randomBetween(34, 48)}%`);
      title.style.setProperty("--home-slice-b-bottom", `${randomBetween(37, 49)}%`);
      title.style.setProperty("--home-slice-c-top", `${randomBetween(68, 79)}%`);
      title.style.setProperty("--home-slice-c-bottom", `${randomBetween(6, 17)}%`);
    };

    const schedule = (first = false) => {
      scheduleTimer = window.setTimeout(() => {
        setSignalVariables(1);
        setSignalActive(true);

        releaseTimer = window.setTimeout(() => {
          setSignalActive(false);

          if (Math.random() > 0.42) {
            echoTimer = window.setTimeout(() => {
              setSignalVariables(0.58);
              setSignalActive(true);
              echoReleaseTimer = window.setTimeout(() => {
                setSignalActive(false);
                schedule();
              }, randomBetween(120, 175));
            }, randomBetween(70, 135));
          } else {
            schedule();
          }
        }, randomBetween(480, 560));
      }, first ? randomBetween(900, 1500) : randomBetween(3900, 7600));
    };

    schedule(true);
    return () => {
      window.clearTimeout(scheduleTimer);
      window.clearTimeout(releaseTimer);
      window.clearTimeout(echoTimer);
      window.clearTimeout(echoReleaseTimer);
      setSignalActive(false);
    };
  }, [motionAllowed]);

  return (
    <>
      <Helmet>
        <title>Khaled Asad | Senior Data Engineer</title>
        <meta
          name="description"
          content="Senior Data Engineer designing reliable cloud and hybrid data platforms across AWS, Hadoop, Snowflake, and Databricks."
        />
      </Helmet>

      <div className="homePage">
        <aside className="homeRail" aria-label="Professional snapshot">
          <div className="homeRailBrand">
            <strong>Khaled Asad</strong>
            <span>Senior Data Engineer</span>
          </div>

          <dl className="homeRailFacts">
            <div className="homeRailFact homeRailFact--current">
              <dt>Current</dt>
              <dd>
                Senior Data Engineer
                <span>Cargill</span>
              </dd>
            </div>
            <div className="homeRailFact homeRailFact--locations">
              <dt>Locations</dt>
              <dd className="homeLocationList">
                <span className="homeLocation homeLocation--atlanta">
                  <GiPeach aria-hidden="true" />
                  <span>Atlanta, Georgia</span>
                </span>
                <span className="homeLocation homeLocation--zadar">
                  <GiStoneTower aria-hidden="true" />
                  <span>Zadar, Croatia</span>
                </span>
              </dd>
            </div>
            <div className="homeRailFact homeRailFact--progress">
              <dt>In progress</dt>
              <dd>Snowflake Data Engineer certification</dd>
            </div>
          </dl>

          <div className="homeRailFooter">
            <span>Cloud data platforms</span>
            <span>Built with care</span>
          </div>
        </aside>

        <section className="homeStage" aria-labelledby="home-title">
          <div className="homeCopy">
            <p className="homeEyebrow">KHALED ASAD // SENIOR DATA ENGINEER</p>
            <h1
              id="home-title"
              ref={titleRef}
              className={`homeTitle${signalActive ? " is-signal-breaking" : ""}`}
            >
              <span className="homeTitleLayer homeTitleLayer--base">
                <span>Hello</span>
                <span>World!</span>
              </span>
              <span className="homeTitleLayer homeTitleLayer--sliceA" aria-hidden="true">
                <span>Hello</span>
                <span>World!</span>
              </span>
              <span className="homeTitleLayer homeTitleLayer--sliceB" aria-hidden="true">
                <span>Hello</span>
                <span>World!</span>
              </span>
              <span className="homeTitleLayer homeTitleLayer--sliceC" aria-hidden="true">
                <span>Hello</span>
                <span>World!</span>
              </span>
            </h1>
            <nav className="homeRoutes" aria-label="Explore the site">
              {routes.map((route, index) => (
                <Link className="homeRoute" to={route.to} key={route.to}>
                  <span className="homeRouteIndex">0{index + 1}</span>
                  <span className="homeRouteCopy">
                    <strong>{route.label}</strong>
                    <span>{route.note}</span>
                  </span>
                  <span className="homeRouteArrow" aria-hidden="true">&#8594;</span>
                </Link>
              ))}
            </nav>

            <div className="homeWorldCard">
              <img src={worldGif} alt="" aria-hidden="true" />
              <div>
                <span>Working across</span>
                <strong>Atlanta / Zadar</strong>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
